import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { PlusIcon } from '@heroicons/react/24/outline'
import { Button } from '../button'
import { Gate } from '../gate'
import { Spinner } from '../spinner'
import { ConfirmDeleteDialog } from '../confirm-delete-dialog'
import { CreateBotModal } from './create-bot-modal'
import { BotTokenRevealModal } from './bot-token-reveal-modal'
import { BOT_PERMISSION_GROUPS } from './bot-permission-groups'
import { PermissionChips, TokenCard, TokenCardGrid } from '../tokens/token-card'
import { botTokenApi } from 'src/services/bot-tokens/api'
import { botTokenQueries, BotTokenCreated } from 'src/services/bot-tokens'
import { useDeleteWithConfirmation } from 'src/hooks/use-delete-with-confirmation'
import { memberQueries } from 'src/services/members/queries'
import { Member } from 'src/services/members/models'
import type { TFunction } from 'i18next'
import { InfoHint } from 'src/components/info-hint'

interface BotTokensSectionProps {
  orgId: string
}

function formatDate(iso: string | null, t: TFunction): string {
  if (!iso) return t('botTokens.never')
  try {
    return format(new Date(iso), 'MMM d, yyyy')
  } catch {
    return '—'
  }
}

/** Distinct module labels the token's permissions touch, for a compact summary. */
export function permissionSummary(permissions: string[], t: TFunction): string {
  if (!permissions.length) return t('botTokens.noPermissions')
  const labels = BOT_PERMISSION_GROUPS.filter((g) =>
    permissions.some(
      (p) => p === g.access?.codename || g.permissions.some((perm) => perm.codename === p),
    ),
  ).map((g) => g.label)
  return labels.length
    ? labels.join(', ')
    : t('botTokens.permissionCount', { count: permissions.length })
}

/**
 * Gated outside the content so members without the permission never mount its
 * queries (the bot-token list is 403 for them).
 */
export const BotTokensSection: React.FC<BotTokensSectionProps> = ({ orgId }) => (
  <Gate permission="organization.manage_roles">
    <BotTokensContent orgId={orgId} />
  </Gate>
)

const BotTokensContent: React.FC<BotTokensSectionProps> = ({ orgId }) => {
  const { t } = useTranslation()
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [revealed, setRevealed] = useState<BotTokenCreated | null>(null)

  const { data, isLoading } = useQuery(botTokenQueries.list({ filters: { organization: orgId } }))
  const { data: members } = useQuery(memberQueries.list(orgId))

  const bots = data?.results ?? []

  // Resolve the human admin that created each bot from the org's member list.
  const nameByUserId = useMemo(() => {
    const map: Record<string, string> = {}
    for (const m of (members ?? []) as Member[]) {
      map[m.userId] = `${m.firstName} ${m.lastName}`.trim()
    }
    return map
  }, [members])

  const { requestDelete, dialogProps } = useDeleteWithConfirmation({
    mutationFn: (botId: string) => botTokenApi.remove(botId),
    queryKey: botTokenQueries.all(),
  })

  return (
    <>
      <div
        className="rounded-lg border border-border bg-surface p-6 shadow-sm"
        id="bot-tokens-section"
        data-testid="bot-tokens-section"
      >
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-text-primary">
              {t('botTokens.title')}
              <InfoHint text={t('botTokens.description')} className="ml-1.5" />
            </h3>
          </div>
          <Button
            variant="primary"
            onClick={() => setIsCreateOpen(true)}
            icon={<PlusIcon className="h-4 w-4" aria-hidden="true" />}
            data-testid="create-bot-button"
          >
            {t('botTokens.createBot')}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8" data-testid="bot-tokens-loading">
            <Spinner size="md" />
          </div>
        ) : bots.length === 0 ? (
          <div
            className="rounded-md border border-border bg-surface p-4 text-center text-sm text-text-placeholder"
            data-testid="bot-tokens-empty"
          >
            {t('botTokens.empty')}
          </div>
        ) : (
          <TokenCardGrid testId="bot-tokens-list">
            {bots.map((bot) => (
              <TokenCard
                key={bot.id}
                testId={`bot-row-${bot.id}`}
                name={bot.name}
                prefix={bot.prefix}
                badge={
                  bot.connectionClientName && (
                    <span
                      className="mt-2 inline-block rounded bg-primary-50 px-2 py-0.5 text-xs text-primary"
                      data-testid={`bot-connection-${bot.id}`}
                    >
                      {t('botTokens.connectionBadge', { client: bot.connectionClientName })}
                    </span>
                  )
                }
                permissionsTitle={t('botTokens.permissions')}
                permissionsTestId={`bot-permissions-${bot.id}`}
                permissions={
                  <PermissionChips
                    permissions={bot.permissions}
                    otherLabel={t('botTokens.otherPermissions')}
                    emptyText={t('botTokens.noPermissions')}
                    groupTestIdPrefix={`bot-permission-chips-${bot.id}`}
                  />
                }
                meta={[
                  { label: t('botTokens.lastUsed'), value: formatDate(bot.lastUsedAt, t) },
                  {
                    label: t('botTokens.createdBy'),
                    value: (bot.createdBy && nameByUserId[bot.createdBy]) || '—',
                  },
                  { label: t('botTokens.created'), value: formatDate(bot.created, t) },
                ]}
                revokeAriaLabel={t('botTokens.revokeBot', { name: bot.name })}
                revokeTitle={t('botTokens.revoke')}
                revokeTestId={`revoke-bot-${bot.id}`}
                onRevoke={() => requestDelete(bot.id, bot.name)}
              />
            ))}
          </TokenCardGrid>
        )}

        {/* Revoke confirmation dialog */}
        <ConfirmDeleteDialog {...dialogProps} />

        <CreateBotModal
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          orgId={orgId}
          onCreated={(created) => {
            setIsCreateOpen(false)
            setRevealed(created)
          }}
        />

        <BotTokenRevealModal
          token={revealed?.token ?? null}
          botName={revealed?.name ?? ''}
          onDismissConfirmed={() => setRevealed(null)}
        />
      </div>
    </>
  )
}
